import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Stack, 
  Button, 
  Link, 
  Divider,
  IconButton,
  useTheme,
  TextField,
  InputAdornment,
  Paper
} from '@mui/material';
import { APP_ROUTES } from '../../utils/constants';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  LinkedIn, 
  Send, 
  LocationOn, 
  Email, 
  Phone 
} from '@mui/icons-material';

/**
 * Enhanced Material UI Footer component with corporate design
 */
const MuiFooter = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  
  const footerLinks = [
    {
      title: 'Company',
      links: [
        { name: 'About', path: APP_ROUTES.ABOUT },
        { name: 'Team', path: APP_ROUTES.ABOUT },
        { name: 'Careers', path: '/careers' },
        { name: 'Contact', path: APP_ROUTES.CONTACT },
      ],
    },
    {
      title: 'Solutions',
      links: [
        { name: 'For Schools', path: '/solutions#schools' },
        { name: 'For Parents', path: '/solutions#parents' },
        { name: 'For Teachers', path: '/solutions#teachers' },
        { name: 'For Mentors', path: '/solutions#mentors' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { name: 'Blog', path: '/blog' },
        { name: 'Events', path: '/events' },
        { name: 'Help Center', path: '/help' },
        { name: 'FAQ', path: '/faq' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', path: APP_ROUTES.PRIVACY_POLICY },
        { name: 'Terms of Service', path: '/terms-of-service' },
        { name: 'Cookie Policy', path: '/cookie-policy' },
      ],
    },
  ];
  
  const socialLinks = [
    { icon: <Facebook />, path: 'https://www.facebook.com/meira.education' },
    { icon: <Twitter />, path: 'https://twitter.com/meira_education' },
    { icon: <Instagram />, path: 'https://www.instagram.com/meira_education' },
    { icon: <LinkedIn />, path: 'https://www.linkedin.com/in/meira-africa-education-solutions' },
  ];
  
  return (
    <Box component="footer" sx={{ bgcolor: 'background.paper', pt: 8, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Main footer content */}
        <Grid container spacing={4}>
          {/* Logo and company info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <img 
                src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/2_xwp1oc.png" 
                alt="Logo" 
                style={{ height: 45 }}
              />
              <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold', color: theme.palette.primary.main }}>
                
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 300 }}>
              Connecting schools, teachers, parents, and mentors to provide quality education for every child.
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Contact Us
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn fontSize="small" color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Nairobi, Kenya
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email fontSize="small" color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    info@meira.co.ke
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone fontSize="small" color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    0757 575 575
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={1}>
                {socialLinks.map((link, index) => (
                  <IconButton
                    key={index}
                    component="a"
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    {link.icon}
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Grid>
          
          {/* Footer links */}
          <Grid item xs={12} md={5} container spacing={2}>
            {footerLinks.map((group) => (
              <Grid item xs={6} sm={3} key={group.title}>
                <Typography 
                  variant="subtitle2" 
                  color="text.primary"
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    display: 'inline-block'
                  }}
                >
                  {group.title}
                </Typography>
                <Stack spacing={1}>
                  {group.links.map((link) => (
                    <Link
                      key={link.name}
                      component="button"
                      variant="body2"
                      color="text.secondary"
                      underline="none"
                      onClick={() => handleNavigation(link.path)}
                      sx={{ 
                        textAlign: 'left',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              </Grid>
            ))}
          </Grid>
          
          {/* Newsletter subscription */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="text.primary" fontWeight="bold" gutterBottom>
              Subscribe to Our Newsletter
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Get the latest news and updates
            </Typography>
            <Paper
              component="form"
              elevation={0}
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <TextField
                size="small"
                placeholder="Your email address"
                variant="standard"
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        color="primary" 
                        aria-label="subscribe" 
                        edge="end"
                        sx={{ 
                          bgcolor: 'primary.main',
                          color: 'white',
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          }
                        }}
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ml: 1, flex: 1 }}
              />
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              By subscribing, you agree to our Privacy Policy
            </Typography>
            
            {/* App buttons would go here if needed */}
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Footer bottom */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'center' } }}>
          <Typography variant="body2" color="text.secondary">
            &copy; {currentYear} Meira Education. All rights reserved.
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }}
            sx={{ mt: { xs: 2, sm: 0 }, alignItems: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              by: Digitaliquids Cloud Technologies
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default MuiFooter; 